import { IPermissions } from '../types/roles.types';

/**
 * Check if the user has a specific permission in a category
 * @param permissions - The user's permissions object
 * @param category - The permission category (e.g., 'team', 'role_management', 'vault')
 * @param permission - The specific permission to check
 * @returns boolean indicating if the user has the permission
 */
export const hasPermission = (
    permissions: IPermissions | null | undefined,
    category: string,
    permission: string
): boolean => {
    if (!permissions) return false;
    
    // Check legacy flat permissions first
    if (permission in permissions) {
        return permissions[permission] || false;
    }
    
    // Then check structured permissions
    return permissions[category]?.[permission] || false;
};

/**
 * Check if the user has any of the specified permissions in a category
 * @param permissions - The user's permissions object
 * @param category - The permission category
 * @param permissionList - Array of permissions to check
 * @returns boolean indicating if the user has any of the permissions
 */
export const hasAnyPermission = (
    permissions: IPermissions | null | undefined,
    category: string,
    permissionList: string[]
): boolean => {
    if (!permissions) return false;
    return permissionList.some(permission => hasPermission(permissions, category, permission));
};

/**
 * Check if the user has all of the specified permissions in a category
 * @param permissions - The user's permissions object
 * @param category - The permission category
 * @param permissionList - Array of permissions to check
 * @returns boolean indicating if the user has all the permissions
 */
export const hasAllPermissions = (
    permissions: IPermissions | null | undefined,
    category: string,
    permissionList: string[]
): boolean => {
    if (!permissions) return false;
    return permissionList.every(permission => hasPermission(permissions, category, permission));
};

/**
 * Get all permissions for a specific category
 * @param permissions - The user's permissions object
 * @param category - The permission category
 * @returns Object containing the permissions for the category
 */
export const getCategoryPermissions = (
    permissions: IPermissions | null | undefined,
    category: string
): Record<string, boolean> => {
    if (!permissions) return {};
    
    // Handle legacy flat permissions
    if (category === 'legacy') {
        return {
            manage_team: permissions.manage_team || false,
            manage_roles: permissions.manage_roles || false,
            manage_all_teams: permissions.manage_all_teams || false,
            invite_members: permissions.invite_members || false,
            remove_members: permissions.remove_members || false
        };
    }
    
    // Handle structured permissions
    return permissions[category] as Record<string, boolean> || {};
};